import {
  AnonymousEnrichmentConfiguration,
  ConditionFieldCatalog,
  DestinationCatalogItem,
  EmailEnrichmentConfiguration,
  TRACKING_EVENT_NAME,
} from '@adtrackify/at-tracking-event-types';
import axios, { AxiosResponse } from 'axios';

import { setupAuthInterceptor } from '../core/auth-interceptor';
import { REQUEST_TIMEOUT_MS } from '../core/constants/request-constants';
import { getAuthorizationConfig } from '../core/auth-config';

import type {
  ConnectGooglePayload,
  PixelDestinationResponse,
  UpdateWhitelistedDomainsPayload,
  WhitelistedDomainsResponse,
} from './types/destinations-api-service-types';
import { DeleteDestinationPayload, IcreateDestinationPayload } from '@/services/internal-api/destinations/types/destinations-api-service-types';

const _destinationsApiService = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL + '/destinations',
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'x-api-key': import.meta.env.VITE_APP_DESTINATIONS_API_KEY,
  },
});

setupAuthInterceptor(_destinationsApiService);

export const DestinationsApiService = {
  getDestinationForPixel: async (
    pixelId: string
  ): Promise<AxiosResponse<PixelDestinationResponse>> => {
    const config = await getAuthorizationConfig();
    return await _destinationsApiService.get(`/?pixelId=${pixelId}`, config);
  },

  destinationList: (
    destinationType?: string
  ): Promise<AxiosResponse<{ items: DestinationCatalogItem[] }>> => {
    return _destinationsApiService.get(
      `/catalog/list${destinationType ? `?destinationType=${destinationType}` : ''}`
    );
  },
  //------------- Start Authorized Endpoints ------------------

  deleteDestination: async (destId: string, data: DeleteDestinationPayload) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.delete(`/${destId}`, { headers: config.headers, data });
  },

  createDestination: async (data: IcreateDestinationPayload) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.post('/', data, config);
  },

  updateDestination: async (destId: string, data: IcreateDestinationPayload) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.patch(`/${destId}`, data, config);
  },

  enableDestination: async (
    destId: string,
    data: { accountId: string; id: string; pixelId: string }
  ) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.post(`/${destId}/enable`, data, config);
  },

  disableDestination: async (
    destId: string,
    data: { accountId: string | undefined; id: string; pixelId: string | undefined }
  ) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.post(`/${destId}/disable`, data, config);
  },

  getDestinationById: async (destId: string) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.get(`/${destId}`, config);
  },

  createOauthDestination: async (data: any) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.post('/', data, config);
  },
  updateAnonymousEnrichment: async (data: { accountId: string; pixelId: string; anonymousEnrichment: AnonymousEnrichmentConfiguration }) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.patch('/contact-acquisition/anonymous-enrichment', data, config);
  },
  updateEmailEnrichment: async (data: { accountId: string; pixelId: string; emailEnrichment: EmailEnrichmentConfiguration }) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.patch('/contact-acquisition/email-enrichment', data, config);
  },
  updateContactAcquisitionSettings: async (data: { accountId: string; pixelId: string; mergeToIdentityTraitsEnabled: boolean }) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.patch('/contact-acquisition/settings', data, config);
  },
  updateWhitelistedDomains: async (
    data: UpdateWhitelistedDomainsPayload
  ): Promise<AxiosResponse<WhitelistedDomainsResponse>> => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.patch('/contact-acquisition/whitelisted-domains', data, config);
  },
  getCustomersList: async (destinationId: string) => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.get(
      `/google/customers-list?destinationId=${destinationId}`,
      config
    );
  },

  connectGoogle: async (data: ConnectGooglePayload): Promise<AxiosResponse<void>> => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.post('/google/connect', data, config);
  },

  getConditionFieldsCatalog: async (
    sourceEventName: string
  ): Promise<AxiosResponse<ConditionFieldCatalog>> => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.get(`/catalog/condition-fields/${sourceEventName}`, config);
  },

  getSupportedSourceEvents: async (): Promise<AxiosResponse<{ events: TRACKING_EVENT_NAME[] }>> => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.get('/catalog/supported-source-events', config);
  },

  testSlackWebhook: async (data: {
    webhookUrl: string;
    template: { blocks: unknown[] };
  }): Promise<AxiosResponse<{ success: boolean }>> => {
    const config = await getAuthorizationConfig();
    return _destinationsApiService.post('/slack/test-webhook', data, config);
  },
};
